import { FormikErrors } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { useDebounce } from '../../common/debounce';
import { FormConstants, FormContactDetailsCheck, REPLACE, WaFormConstants } from '../GenericForm/Components/Constants';
import { capitalizeFirstLetter } from '../../common/helper/helper';

export enum AUTOCOMPLETE_FORM_TYPE {
	DonateForm,
	EventForm
}

export enum ENTITY_TYPE {
	Company = 'company',
	Individual = 'individual'
}

const AUTOCOMPLETE_URL_MAP: Record<AUTOCOMPLETE_FORM_TYPE, string> = {
	[AUTOCOMPLETE_FORM_TYPE.DonateForm]: 'DonateForm/GetDonorDataCheck',
	[AUTOCOMPLETE_FORM_TYPE.EventForm]: 'EventRegForm/GetEventDataCheck'
};

const CompanyAutocompleteFields = {
	CompanyName: WaFormConstants.companyName,
	ABN: WaFormConstants.abn,
	CompanyAddress: WaFormConstants.companyAddress,
	CompanySuburb: WaFormConstants.companySuburb,
	CompanyState: WaFormConstants.companyState,
	CompanyPostCode: WaFormConstants.companyPostcode,
	ContactFirstName: WaFormConstants.contactFirstName,
	ContactLastName: WaFormConstants.contactLastName,
	ContactPhone: WaFormConstants.contactPhone
};

const IndividualAutocompleteFields = {
	FirstName: FormConstants.firstName,
	LastName: FormConstants.lastName,
	Address: FormConstants.address,
	Phone: FormConstants.phone,
	Mobile: FormConstants.mobile,
	State: FormConstants.state,
	Suburb: FormConstants.suburb,
	Postcode: FormConstants.postcode
};

type AutoCompleteQueryHookProps<T> = {
	emailAddress: string;
	contactEmailAddress: string;
	entityType: string;
	autocompleteType: AUTOCOMPLETE_FORM_TYPE;
	errors: FormikErrors<T>;
	setFieldValue: (field: string, value: unknown) => void;
	setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
	skip?: boolean;
};

export function useAutoComplete<T>(input: AutoCompleteQueryHookProps<T>) {
	const { emailAddress, entityType, autocompleteType, contactEmailAddress, setFieldTouched, setFieldValue, skip, errors } = input;
	const [error, setError] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);
	const [autocompletedFields, setAutocompletedFields] = useState<FormContactDetailsCheck>(undefined);

	const autocompleteUrl = AUTOCOMPLETE_URL_MAP[autocompleteType];

	const emailAddressToUse = entityType === ENTITY_TYPE.Company ? contactEmailAddress : emailAddress;
	const emailError = entityType === ENTITY_TYPE.Company ? errors[WaFormConstants.contactEmail] : errors[FormConstants.email];

	const debouncedEmailAddressToUse = useDebounce(emailAddressToUse, 2000);

	const fetchData = useCallback(async () => {
		const skipFetch = debouncedEmailAddressToUse === '' || debouncedEmailAddressToUse === undefined || skip || emailError;
		if (!skipFetch) {
			try {
				setLoading(true);
				setError('');
				const res = await fetch(
					`/Umbraco/Api/${autocompleteUrl}?emailAddress=${debouncedEmailAddressToUse}&entityType=${entityType}`,
					{
						method: 'GET',
						headers: {
							Accept: 'application/json',
							'Content-Type': 'application/json'
						}
					}
				);
				if (res.ok) {
					const response = await res.json();
					const fixedResponse = Object.keys(response).reduce(
						(prev, curr) => ({
							...prev,
							...{ [curr === 'abn' ? curr.toUpperCase() : capitalizeFirstLetter(curr)]: response[curr] }
						}),
						{} as FormContactDetailsCheck
					);
					setLoading(false);
					if (fixedResponse !== null) {
						if (fixedResponse.EntityType === ENTITY_TYPE.Company) {
							Object.entries(CompanyAutocompleteFields).forEach(([completeField, formField]) => {
								if (fixedResponse[completeField] === true) {
									setFieldValue(formField, REPLACE);
									setFieldTouched(formField, true, true);
								}
							});
						}
						if (fixedResponse.EntityType === ENTITY_TYPE.Individual) {
							Object.entries(IndividualAutocompleteFields).forEach(([completeField, formField]) => {
								if (fixedResponse[completeField] === true) {
									setFieldValue(formField, REPLACE);
									setFieldTouched(formField, true, true);
								}
							});
						}
						setAutocompletedFields(fixedResponse);
					}
				} else {
					const err = await res.text();
					setLoading(false);
					setError(err);
				}
			} catch (e) {
				setLoading(false);
				setError('Error: Something went wrong with the query');
			}
		}
	}, [debouncedEmailAddressToUse, entityType]);

	useEffect(() => {
		fetchData();
		return () => {
			setError('');
			setLoading(false);
			setAutocompletedFields(undefined);
		};
	}, [debouncedEmailAddressToUse]);

	const refetch = () => fetchData();

	useEffect(() => {
		if (autocompletedFields) {
			setFieldValue(FormConstants.address, '');
			setFieldValue(FormConstants.firstName, '');
			setFieldValue(FormConstants.lastName, '');
			setFieldValue(FormConstants.phone, '');
			setFieldValue(FormConstants.postcode, '');
			setFieldValue(FormConstants.state, '');
			setFieldValue(FormConstants.suburb, '');
			setFieldValue(FormConstants.mobile, '');
			setFieldValue(WaFormConstants.companyName, '');
			setFieldValue(WaFormConstants.abn, '');
			setFieldValue(WaFormConstants.companyAddress, '');
			setFieldValue(WaFormConstants.companySuburb, '');
			setFieldValue(WaFormConstants.companyState, '');
			setFieldValue(WaFormConstants.companyPostcode, '');
			setFieldValue(WaFormConstants.contactFirstName, '');
			setFieldValue(WaFormConstants.contactLastName, '');
			setFieldValue(WaFormConstants.contactPhone, '');
			setAutocompletedFields(undefined);
		}
	}, [emailAddressToUse, entityType]);

	return { autocompletedFields, loading, error, refetch, setAutocompletedFields };
}
